"use client";

// La mise en page commune à l'accueil (l'entreprise) et à la vue d'un projet, en quatre colonnes comme un IDE : les
// agents (un fil chacun), le chat ouvert, le contenu (des onglets), et le panneau de droite qui suit l'onglet ouvert.
// L'atelier tient la liste des fils (rafraîchie : un agent peut écrire à un autre), les largeurs des colonnes
// retenues d'une visite à l'autre, et le chat ; le contenu et le panneau viennent de la page.

import dynamic from "next/dynamic";
import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  api,
  CLE_LARGEURS,
  cleFil,
  ErreurApi,
  type Config,
  type Fil,
  type Portee,
} from "@/lib/api";
import { ilYa } from "@/lib/dates";
import type { ChatHandle, ReferencesChat } from "./Chat";
import { Briques } from "./DemanderAgent";
import { EnDirect } from "./EnDirect";
import { Redimensionneur } from "./Redimensionneur";
import { SqueletteChat, SqueletteFils } from "./Squelette";
import ThemeToggle from "./ThemeToggle";

// ChatKit est un élément web : il ne se rend que dans le navigateur.
const Chat = dynamic(() => import("./Chat").then((m) => m.Chat), {
  ssr: false,
  loading: () => <SqueletteChat />,
});

type Colonne = "chats" | "chat" | "arbre";

// Les largeurs de départ, et la place que le document garde toujours.
const LARGEURS = { chats: 210, chat: 420, arbre: 280 };
const DOCUMENT_MIN = 360;

// Ce qu'une largeur enregistrée peut valoir : au-delà, la mise en page ne tiendrait plus dans la fenêtre.
const BORNES: Record<Colonne, [number, number]> = {
  chats: [140, 900],
  chat: [300, 1600],
  arbre: [200, 900],
};

// La liste des fils se relit souvent quand un agent travaille (il peut écrire à un autre), moins sinon.
const RAFRAICHIR_OCCUPE = 4000;
const RAFRAICHIR_CALME = 15000;

// Le volet du chat (la liste des chats et le chat) se ferme pour donner sa place au document ; il reste fermé d'une
// visite à l'autre. Une demande à l'agent le rouvre : ouvrirChat() le dit à l'atelier par un événement, et rend la
// main une fois le chat affiché (le compositeur ne prend pas le focus tant qu'il est caché).
const CLE_CHAT_FERME = "minibreek:chat-ferme";

// Les réponses lues de chaque fil, toutes portées confondues : relues et réécrites à chaque fois, puisque plusieurs
// listes de fils (l'entreprise, un projet) les tiennent à jour.
const CLE_LUS = "minibreek:lus";
function lireLus(): Record<string, number> | null {
  try {
    const brut = window.localStorage.getItem(CLE_LUS);
    return brut ? (JSON.parse(brut) as Record<string, number>) : null;
  } catch {
    return null;
  }
}
function ecrireLus(lus: Record<string, number>) {
  try {
    window.localStorage.setItem(CLE_LUS, JSON.stringify(lus));
  } catch {
    // Sans stockage local, rien ne se compte d'une visite à l'autre.
  }
}
const OUVRIR_CHAT = "minibreek:ouvrir-chat";
export function ouvrirChat(): Promise<void> {
  window.dispatchEvent(new Event(OUVRIR_CHAT));
  return new Promise((ok) => window.setTimeout(ok, 40));
}

// Les largeurs retenues d'une visite à l'autre, tolérantes à un contenu absent ou abîmé.
function largeursEnregistrees(): typeof LARGEURS | null {
  try {
    const brut = window.localStorage.getItem(CLE_LARGEURS);
    if (!brut) return null;
    const lu: unknown = JSON.parse(brut);
    if (typeof lu !== "object" || lu === null) return null;
    const largeurs = { ...LARGEURS };
    for (const colonne of Object.keys(BORNES) as Colonne[]) {
      const valeur = (lu as Record<string, unknown>)[colonne];
      if (typeof valeur !== "number" || !Number.isFinite(valeur)) continue;
      const [min, max] = BORNES[colonne];
      largeurs[colonne] = Math.min(max, Math.max(min, Math.round(valeur)));
    }
    return largeurs;
  } catch {
    return null;
  }
}

// Le contrôleur de navigation du contenu : l'endroit ouvert et l'historique, pour les boutons précédent et suivant.
// Un endroit, c'est un onglet, ce qui y est ouvert (le dossier ou la pièce) et, dedans, l'endroit lu (le chapitre du
// mémoire, l'arbitrage choisi) : précédent revient d'abord sur ses pas dans le document, avant d'en sortir.
export type Lieu<O extends string> = {
  onglet: O; // l'onglet affiché
  detail: string | null; // ce qui y est ouvert : un chemin du dossier, une pièce du DCE
  ancre: string | null; // l'endroit lu dans ce qui est ouvert
};

export type Navigation<O extends string> = Lieu<O> & {
  passe: Lieu<O>[]; // l'historique, pour le bouton précédent
  futur: Lieu<O>[]; // ce que le bouton suivant peut rouvrir
};

const ici = <O extends string>({
  onglet,
  detail,
  ancre,
}: Lieu<O>): Lieu<O> => ({
  onglet,
  detail,
  ancre,
});
const meme = <O extends string>(a: Lieu<O>, b: Lieu<O>) =>
  a.onglet === b.onglet && a.detail === b.detail && a.ancre === b.ancre;

// `pas` endroits en arrière (négatif) ou en avant, autant que l'historique en a.
function deplacer<O extends string>(
  n: Navigation<O>,
  pas: number,
): Navigation<O> {
  for (; pas < 0 && n.passe.length; pas++) {
    const lieu = n.passe.at(-1)!;
    n = { ...lieu, passe: n.passe.slice(0, -1), futur: [ici(n), ...n.futur] };
  }
  for (; pas > 0 && n.futur.length; pas--) {
    const lieu = n.futur[0];
    n = { ...lieu, passe: [...n.passe, ici(n)], futur: n.futur.slice(1) };
  }
  return n;
}

// L'historique du navigateur double celui du contenu : chaque endroit y a son entrée, qui porte son rang. Le retour du
// navigateur (⌘[, le glisser à deux doigts) fait donc comme le bouton précédent, et ne quitte la page qu'au bout de
// l'historique du contenu. Une entrée devenue sans objet (l'onglet d'un fichier fermé, une page rechargée) est sautée.
const RANG = "minibreek";

// `dansUrl` : les onglets que l'adresse de la page porte (`?onglet=…`, rien pour celui de départ), pour qu'un lien ou
// un rechargement rouvre le bon. Sans, l'adresse ne change pas.
export function useNavigation<O extends string>(
  depart: O,
  dansUrl?: readonly O[],
) {
  const valides = useRef(dansUrl);
  const adresse = useCallback(
    (onglet: O) => {
      if (!valides.current) return undefined; // l'adresse reste
      const u = new URL(window.location.href);
      if (onglet === depart) u.searchParams.delete("onglet");
      else u.searchParams.set("onglet", onglet);
      return `${u.pathname}${u.search}${u.hash}`;
    },
    [depart],
  );
  const [nav, setNav] = useState<Navigation<O>>({
    onglet: depart,
    detail: null,
    ancre: null,
    passe: [],
    futur: [],
  });
  const navRef = useRef(nav);
  navRef.current = nav;
  const rang = useRef(0); // le rang de l'entrée du navigateur où l'on est

  useEffect(() => {
    const page = window.location.pathname;
    const rangDe = (etat: unknown) => {
      const r = (etat as Record<string, unknown> | null)?.[RANG] as
        { page: string; rang: number } | undefined;
      return r?.page === page ? r.rang : null;
    };
    // L'onglet que porte l'adresse (celui de départ sans paramètre), s'il en porte.
    const lu = (): O | null => {
      if (!valides.current) return null;
      const o = new URLSearchParams(window.location.search).get("onglet") as O;
      return o && valides.current.includes(o) ? o : depart;
    };
    // Ouvert d'emblée (après le premier rendu : le serveur ne connaît pas l'adresse).
    const demande = lu();
    if (demande && demande !== depart)
      setNav((n) => ({ ...n, onglet: demande }));
    const garde = rangDe(window.history.state);
    if (garde !== null) rang.current = garde;
    else
      window.history.replaceState(
        { ...window.history.state, [RANG]: { page, rang: rang.current } },
        "",
      );
    const surRetour = (e: PopStateEvent) => {
      const r = rangDe(e.state);
      if (r === null) return; // une autre page : le routeur s'en charge
      const pas = r - rang.current;
      rang.current = r;
      const n = navRef.current;
      const voulu = lu();
      // Une entrée d'avant le rechargement : le contenu n'en a plus l'historique, l'adresse dit l'onglet.
      if (
        voulu &&
        voulu !== n.onglet &&
        ((pas < 0 && !n.passe.length) || (pas > 0 && !n.futur.length))
      )
        setNav((n) => ({ ...n, onglet: voulu, detail: null, ancre: null }));
      else if (pas < 0 && !n.passe.length) window.history.back();
      else if (pas > 0 && !n.futur.length) window.history.forward();
      else setNav((n) => deplacer(n, pas));
    };
    window.addEventListener("popstate", surRetour);
    return () => window.removeEventListener("popstate", surRetour);
  }, []);

  const allerA = useCallback(
    (onglet: O, detail: string | null = null, ancre: string | null = null) => {
      const lieu = { onglet, detail, ancre };
      if (meme(navRef.current, lieu)) return;
      rang.current += 1;
      window.history.pushState(
        {
          ...window.history.state,
          [RANG]: { page: window.location.pathname, rang: rang.current },
        },
        "",
        adresse(onglet),
      );
      setNav((n) =>
        meme(n, lieu) ? n : { ...lieu, passe: [...n.passe, ici(n)], futur: [] },
      );
    },
    [adresse],
  );

  // Corrige l'endroit ouvert sans en faire une étape de l'historique (un fichier renommé, un dossier supprimé).
  const remplacer = useCallback(
    (onglet: O, detail: string | null = null, ancre: string | null = null) => {
      if (valides.current)
        window.history.replaceState(window.history.state, "", adresse(onglet));
      setNav((n) => ({ ...n, onglet, detail, ancre }));
    },
    [adresse],
  );

  // Les boutons passent par le navigateur, pour que les deux historiques restent alignés.
  const precedent = useCallback(() => window.history.back(), []);
  const suivant = useCallback(() => window.history.forward(), []);

  return { nav, setNav, allerA, remplacer, precedent, suivant };
}

// La barre du contenu : précédent, suivant, puis les onglets de la page.
export function BarreNavigation<O extends string>({
  nav,
  precedent,
  suivant,
  libelle,
  children,
  actions,
}: {
  nav: Navigation<O>;
  precedent: () => void;
  suivant: () => void;
  libelle: string;
  children: ReactNode;
  actions?: ReactNode; // au bout de la barre, hors du défilement des onglets
}) {
  return (
    <nav className="navigation" aria-label={libelle}>
      <button
        className="nav-bouton"
        title="Précédent"
        aria-label="Précédent"
        disabled={nav.passe.length === 0}
        onClick={precedent}
      >
        ←
      </button>
      <button
        className="nav-bouton"
        title="Suivant"
        aria-label="Suivant"
        disabled={nav.futur.length === 0}
        onClick={suivant}
      >
        →
      </button>
      <span className="nav-sep" />
      <div className="onglets">{children}</div>
      {actions}
    </nav>
  );
}

// Un agent toujours là, épinglé en tête des chats.
export type Epingle = {
  fil: string;
  classe?: string; // sa couleur sur l'épingle (veille.css), sinon celle de la grue
  carte: (occupe: boolean) => ReactNode;
  entete: ReactNode;
  direct: ReactNode; // au-dessus de son fil ouvert : les tours qu'il a confiés, en direct
};

export function Atelier({
  portee,
  config,
  entete,
  frise,
  erreur,
  onErreur,
  titreFils,
  roles,
  poignee,
  chapitre,
  navigation,
  document,
  panneau,
  sansPanneau = false,
  panneauVoulu = false,
  libellePanneau,
  onEffet,
  onDebut,
  onFin,
  references,
  epingle,
}: {
  portee: Portee; // un objet stable (useMemo) : la liste des fils en dépend
  config: Config | null; // pas encore chargée : l'atelier s'affiche quand même, le chat attend
  entete: ReactNode; // la gauche de l'en-tête ; le choix du thème est à droite
  frise?: ReactNode; // l'avancement du projet, entre le titre et le choix du thème
  erreur: string | null;
  onErreur: (message: string) => void;
  titreFils: string;
  roles?: { nom: string; texte: string }[]; // ce que « nouveau » propose ; sans rôles, il ouvre un fil vide
  poignee: RefObject<ChatHandle | null>;
  chapitre: string | null; // le chapitre ouvert, que le chat d'un projet passe à l'agent
  navigation: ReactNode;
  document: ReactNode;
  panneau: ReactNode;
  sansPanneau?: boolean; // une vue pleine largeur (MCP Apps) n'a pas de panneau
  panneauVoulu?: boolean; // demandé (le bouton de l'explorateur) : il reste là, même dans une fenêtre étroite
  libellePanneau: string;
  onEffet: (nom: string, data?: Record<string, unknown>) => void;
  onDebut: () => void;
  onFin: () => void;
  references?: ReferencesChat; // les fichiers ouverts et l'ouverture des références (Chat)
  // Les agents toujours là (l'Orchestrateur et la Veille, l'Agent du projet) : leur fil en tête de liste, sans croix,
  // avec sa carte et son en-tête.
  epingle?: Epingle | (Epingle | null)[] | null;
}) {
  const epingles = (Array.isArray(epingle) ? epingle : [epingle]).filter(
    (e): e is Epingle => !!e,
  );
  const [fils, setFils] = useState<Fil[] | null>(null); // null : pas encore chargée
  const [fil, setFil] = useState<string | null>(null);
  const epingleOuvert = epingles.find((e) => e.fil === fil);
  const [menu, setMenu] = useState(false); // le menu « nouveau » ouvert
  const [largeurs, setLargeurs] = useState(LARGEURS);
  const largeursPretes = useRef(false);
  const [chatFerme, setChatFerme] = useState(false);
  const corps = useRef<HTMLDivElement>(null);
  const filRef = useRef(fil);
  filRef.current = fil;
  // Ce que le chat de cette page a fait du fil ouvert : pendant un tour, ChatKit reçoit lui-même les messages.
  const tourLocal = useRef(false);
  const [tourIci, setTourIci] = useState(false); // le même, pour l'affichage : ChatKit montre déjà ce tour
  // Le dernier `maj` vu de chaque fil : le fil ouvert qui a reçu un message d'ailleurs se relit.
  const vus = useRef<Map<string, string> | null>(null);
  // Les réponses lues de chaque fil (stockage local) : le reste se compte « non lues » dans la liste.
  const [lus, setLus] = useState<Record<string, number> | null>(null);

  const chargerFils = useCallback(async () => {
    let liste: Fil[];
    try {
      liste = await api.fils(portee);
    } catch {
      return; // l'erreur de l'API est déjà montrée par le chargement de la page
    }
    setFils(liste);
    const connus = vus.current ?? new Map<string, string>();
    for (const f of liste) {
      const avant = connus.get(f.id);
      // Un autre agent a écrit dans le fil ouvert : ChatKit le relit.
      if (
        f.id === filRef.current &&
        avant !== undefined &&
        f.maj !== avant &&
        !tourLocal.current
      )
        poignee.current?.rafraichir();
      connus.set(f.id, f.maj);
    }
    vus.current = connus;
  }, [portee, poignee]);

  // Ce qui est lu : la première fois, tout ce qui existe ; ensuite, le fil ouvert (volet du chat ouvert), au fil de
  // ses réponses. Un fil apparu depuis compte toutes les siennes.
  useEffect(() => {
    if (!fils) return;
    const stockes = lireLus();
    const suite = { ...(stockes ?? {}) };
    let change = false;
    for (const f of fils) {
      const vu = stockes === null || (f.id === fil && !chatFerme);
      if (vu && suite[f.id] !== f.reponses) {
        suite[f.id] = f.reponses;
        change = true;
      }
    }
    if (change || stockes === null) ecrireLus(suite);
    setLus(suite);
  }, [fils, fil, chatFerme]);
  const nonLues = (f: Fil): number =>
    lus && !(f.id === fil && !chatFerme)
      ? Math.max(0, f.reponses - (lus[f.id] ?? 0))
      : 0;

  useEffect(() => {
    void chargerFils();
    setFil(window.localStorage.getItem(cleFil(portee)));
  }, [portee, chargerFils]);

  // Les largeurs des colonnes sont lues après le montage : le premier rendu reste identique au serveur, et la
  // mise en page enregistrée s'applique aussitôt après.
  useEffect(() => {
    const enregistrees = largeursEnregistrees();
    if (enregistrees) setLargeurs(enregistrees);
    largeursPretes.current = true;
    try {
      setChatFerme(window.localStorage.getItem(CLE_CHAT_FERME) === "1");
    } catch {
      // Sans stockage local, le volet part ouvert.
    }
  }, []);

  const volet = useCallback((ferme: boolean) => {
    setChatFerme(ferme);
    try {
      if (ferme) window.localStorage.setItem(CLE_CHAT_FERME, "1");
      else window.localStorage.removeItem(CLE_CHAT_FERME);
    } catch {
      // Le choix vaut pour la visite.
    }
  }, []);

  useEffect(() => {
    const ouvrir = () => volet(false);
    window.addEventListener(OUVRIR_CHAT, ouvrir);
    return () => window.removeEventListener(OUVRIR_CHAT, ouvrir);
  }, [volet]);

  // Pendant un glissement, les largeurs changent à chaque pixel : on n'écrit qu'une fois le geste au repos.
  useEffect(() => {
    if (!largeursPretes.current) return;
    const minuterie = window.setTimeout(() => {
      try {
        window.localStorage.setItem(CLE_LARGEURS, JSON.stringify(largeurs));
      } catch {
        // Un navigateur qui refuse le stockage local n'empêche pas de travailler.
      }
    }, 200);
    return () => window.clearTimeout(minuterie);
  }, [largeurs]);

  // La liste des fils suit ce qui se passe ailleurs : les autres onglets, le nom qu'un agent se donne après sa
  // première réponse, et les messages qu'un agent envoie à un autre.
  const occupe = fils?.some((f) => f.occupe) ?? false;
  const filOuvert = fils?.find((f) => f.id === fil);
  useEffect(() => {
    const rafraichir = () => {
      if (window.document.visibilityState === "visible") void chargerFils();
    };
    const minuterie = window.setInterval(
      rafraichir,
      occupe ? RAFRAICHIR_OCCUPE : RAFRAICHIR_CALME,
    );
    window.document.addEventListener("visibilitychange", rafraichir);
    window.addEventListener("focus", rafraichir);
    return () => {
      window.clearInterval(minuterie);
      window.document.removeEventListener("visibilitychange", rafraichir);
      window.removeEventListener("focus", rafraichir);
    };
  }, [chargerFils, occupe]);

  const debut = useCallback(() => {
    tourLocal.current = true;
    setTourIci(true);
    onDebut();
    void chargerFils();
    // Un fil neuf n'existe côté API qu'une fois le tour lancé : on le relit un peu après pour le voir travailler.
    window.setTimeout(() => void chargerFils(), 1500);
  }, [onDebut, chargerFils]);

  const fin = useCallback(() => {
    tourLocal.current = false;
    setTourIci(false);
    onFin();
    void chargerFils(); // le nom du fil se fixe après la première réponse
  }, [onFin, chargerFils]);

  // L'agent a créé un fil (premier message) ou ChatKit en a ouvert un : la liste se met à jour.
  const onFil = useCallback(
    (threadId: string | null) => {
      setFil(threadId);
      void chargerFils();
    },
    [chargerFils],
  );

  const ouvrirFil = useCallback(
    (threadId: string | null) => {
      setFil(threadId);
      setMenu(false);
      return poignee.current?.ouvrir(threadId) ?? Promise.resolve();
    },
    [poignee],
  );

  // Un rôle de départ : un fil neuf, et le compositeur pré-rempli de sa mission.
  const commencer = useCallback(
    (texte: string) => {
      void ouvrirFil(null).then(() => poignee.current?.proposer(texte));
    },
    [ouvrirFil, poignee],
  );

  const supprimerFil = useCallback(
    async (f: Fil) => {
      if (
        !window.confirm(
          `Supprimer la conversation « ${f.titre || "sans titre"} » ?`,
        )
      )
        return;
      try {
        await api.supprimerFil(portee, f.id);
        if (f.id === fil) void ouvrirFil(null);
        await chargerFils();
      } catch (e) {
        onErreur(e instanceof ErreurApi ? e.message : "L'API ne répond pas.");
      }
    },
    [portee, fil, ouvrirFil, chargerFils, onErreur],
  );

  // Le menu « nouveau » se ferme à Échap et au clic ailleurs.
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const touche = (e: KeyboardEvent) => e.key === "Escape" && setMenu(false);
    const clic = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenu(false);
    };
    window.addEventListener("keydown", touche);
    window.addEventListener("mousedown", clic);
    return () => {
      window.removeEventListener("keydown", touche);
      window.removeEventListener("mousedown", clic);
    };
  }, [menu]);

  // Une colonne s'élargit aux dépens du document, qui garde toujours sa place minimale. Les colonnes retirées par
  // les media queries ne comptent pas.
  const ajuster = useCallback((colonne: Colonne, dx: number) => {
    const cache = (requete: string) => window.matchMedia(requete).matches;
    setLargeurs((l) => {
      const place = corps.current?.clientWidth ?? 1400;
      const chats = cache("(max-width: 900px)") ? 0 : l.chats;
      const arbre = cache("(max-width: 1200px)") ? 0 : l.arbre;
      if (colonne === "chats") {
        const max = Math.max(140, place - l.chat - arbre - DOCUMENT_MIN);
        return { ...l, chats: Math.min(max, Math.max(140, l.chats + dx)) };
      }
      if (colonne === "chat") {
        const max = Math.max(300, place - chats - arbre - DOCUMENT_MIN);
        return { ...l, chat: Math.min(max, Math.max(300, l.chat + dx)) };
      }
      const max = Math.max(200, place - chats - l.chat - DOCUMENT_MIN);
      return { ...l, arbre: Math.min(max, Math.max(200, l.arbre - dx)) };
    });
  }, []);

  const remettre = useCallback((colonne: Colonne) => {
    setLargeurs((l) => ({ ...l, [colonne]: LARGEURS[colonne] }));
  }, []);

  // Le modèle du compositeur suit le fil ouvert : Kanon garde le niveau de chaque fil (`session.niveau`), on le relit
  // donc ici. Une conversation neuve part du niveau par défaut (balanced).
  const modele = fils?.find((f) => f.id === fil)?.niveau ?? config?.niveau;
  // Les largeurs vivent en variables CSS : les media queries peuvent encore retirer des colonnes.
  const colonnes = {
    "--largeur-chats": `${largeurs.chats}px`,
    "--largeur-chat": `${largeurs.chat}px`,
    "--largeur-arbre": `${largeurs.arbre}px`,
  } as CSSProperties;

  return (
    <div className="espace">
      <header className="espace-entete">
        <div>{entete}</div>
        {frise && <div className="entete-frise">{frise}</div>}
        <ThemeToggle />
      </header>
      {config?.sandbox === "e2b" && !config.cles.e2b && (
        <p className="bandeau danger">
          E2B_API_KEY manque : l&apos;agent ne peut pas démarrer son bac à
          sable.
        </p>
      )}
      {config?.sandbox === "e2b" && !config.cles.anthropic && (
        <p className="bandeau info">
          ANTHROPIC_API_KEY manque : claude-code ne pourra pas répondre dans
          E2B.
        </p>
      )}
      {config && !config.cles.openrouter && (
        <p className="bandeau info">
          OPENROUTER_API_KEY manque : goose (speed, fast) ne pourra pas
          répondre.
        </p>
      )}
      {erreur && <p className="bandeau danger">{erreur}</p>}

      <div
        className={`espace-corps${chatFerme ? " chat-ferme" : ""}`}
        ref={corps}
        style={colonnes}
      >
        {/* Le volet fermé : un rail avec les briques, qui le rouvre. Un point dit qu'un agent travaille. */}
        <div className="chat-rail">
          <button
            className="chat-rouvrir"
            title="Ouvrir le chat"
            aria-label="Ouvrir le volet du chat"
            onClick={() => volet(false)}
          >
            <Briques />
            {occupe && <span className="chat-rail-occupe" aria-hidden />}
            <span className="chat-rail-nom" aria-hidden>
              {titreFils}
            </span>
          </button>
        </div>
        <aside className="espace-fils">
          <div className="fils-entete" ref={menuRef}>
            <strong>{titreFils}</strong>
            <button
              className="lien"
              title={roles ? "Nouvel agent" : "Nouvelle conversation"}
              aria-label={roles ? "Nouvel agent" : "Nouvelle conversation"}
              aria-expanded={roles ? menu : undefined}
              aria-haspopup={roles ? "menu" : undefined}
              onClick={() => (roles ? setMenu(!menu) : void ouvrirFil(null))}
            >
              +
            </button>
            {roles && menu && (
              <div className="fils-menu" role="menu" aria-label="Nouvel agent">
                <p className="fils-menu-aide">
                  Un rôle pré-remplit sa mission ; on l&apos;ajuste avant
                  d&apos;envoyer.
                </p>
                {roles.map((r) => (
                  <button
                    key={r.nom}
                    role="menuitem"
                    autoFocus={r === roles[0]}
                    onClick={() => commencer(r.texte)}
                  >
                    {r.nom}
                  </button>
                ))}
                <button
                  role="menuitem"
                  className="fils-menu-libre"
                  onClick={() => void ouvrirFil(null)}
                >
                  Conversation libre
                </button>
              </div>
            )}
          </div>
          {fils === null && <SqueletteFils />}
          <ul className="fils-liste" hidden={fils === null}>
            {epingles.map((ep) => {
              const f = fils?.find((x) => x.id === ep.fil);
              const n = f ? nonLues(f) : 0;
              return (
                <li
                  key={ep.fil}
                  className={`fil epingle ${ep.classe ?? ""} ${ep.fil === fil ? "actif" : ""} ${n > 0 ? "non-lu" : ""}`}
                  onClick={() => void ouvrirFil(ep.fil)}
                >
                  {n > 0 && (
                    <span
                      className="fil-pastille"
                      title={`${n} réponse${n > 1 ? "s" : ""} non lue${n > 1 ? "s" : ""}`}
                    >
                      {n}
                    </span>
                  )}
                  <button
                    className="fil-titre"
                    aria-current={ep.fil === fil ? "true" : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      void ouvrirFil(ep.fil);
                    }}
                  >
                    {ep.carte(f?.occupe ?? false)}
                  </button>
                </li>
              );
            })}
            {fils
              ?.filter((f) => !epingles.some((ep) => ep.fil === f.id))
              .map((f) => (
                <li
                  key={f.id}
                  className={`fil ${f.id === fil ? "actif" : ""} ${f.occupe ? "occupe" : ""} ${nonLues(f) > 0 ? "non-lu" : ""}`}
                  title={
                    f.occupe
                      ? `${f.niveau} · travaille`
                      : `${f.niveau} · ${ilYa(f.maj || f.creee)}`
                  }
                  onClick={() => void ouvrirFil(f.id)}
                >
                  {f.occupe && (
                    <span className="fil-chantier" aria-hidden>
                      <i />
                      <i />
                      <i />
                      <i />
                      <i />
                    </span>
                  )}
                  <button
                    className="fil-titre"
                    aria-current={f.id === fil ? "true" : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      void ouvrirFil(f.id);
                    }}
                  >
                    {f.titre || "Nouvelle conversation"}
                  </button>
                  {/* Un agent générique tient sur une ligne, son nom : le mur de briques dit qu'il travaille, la
                      pastille ce qui n'est pas lu, l'infobulle son niveau et son dernier message. */}
                  {!f.occupe && nonLues(f) > 0 && (
                    <span
                      className="fil-pastille"
                      title={`${nonLues(f)} réponse${nonLues(f) > 1 ? "s" : ""} non lue${nonLues(f) > 1 ? "s" : ""}`}
                    >
                      {nonLues(f)}
                    </span>
                  )}
                  <button
                    className="fil-supprimer"
                    title="Supprimer la conversation"
                    aria-label={`Supprimer « ${f.titre || "Nouvelle conversation"} »`}
                    onClick={(e) => {
                      e.stopPropagation();
                      void supprimerFil(f);
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
          </ul>
          {fils?.length === 0 && (
            <p className="discret fils-vide">
              {roles
                ? "Aucun agent encore : « + » en lance un, avec un rôle ou sans."
                : "Aucune conversation."}
            </p>
          )}
        </aside>
        <Redimensionneur
          className="sep-chats"
          libelle="Largeur de la liste des chats"
          onDelta={(dx) => ajuster("chats", dx)}
          onReset={() => remettre("chats")}
        />
        {/* Le chat a son en-tête : la conversation ouverte, et la croix qui ferme le volet (liste comprise). */}
        <aside className="espace-chat">
          <div className="chat-entete">
            <span className="chat-entete-titre">
              {epingleOuvert
                ? epingleOuvert.entete
                : fils?.find((f) => f.id === fil)?.titre ||
                  "Nouvelle conversation"}
            </span>
            <button
              className="chat-fermer"
              title="Fermer le chat"
              aria-label="Fermer le volet du chat"
              onClick={() => volet(true)}
            >
              ×
            </button>
          </div>
          {/* Ce que ChatKit ne montre pas : un tour qu'un autre agent a demandé dans ce fil, ou ceux que
              l'Orchestrateur a confiés, quand son fil est ouvert. */}
          <div className="chat-directs">
            {epingleOuvert?.direct}
            {filOuvert?.occupe && !tourIci && (
              <EnDirect
                fil={filOuvert.id}
                titre={filOuvert.titre || "L'agent"}
                sousTitre="travaille dans ce fil"
              />
            )}
          </div>
          <div className="chat-corps">
            {config && modele ? (
              <Chat
                poignee={poignee}
                portee={portee}
                chapitre={chapitre}
                modele={modele}
                config={config}
                onEffet={onEffet}
                onDebut={debut}
                onFin={fin}
                onFil={onFil}
                references={references}
              />
            ) : (
              <SqueletteChat />
            )}
          </div>
        </aside>
        <Redimensionneur
          className="sep-chat"
          libelle="Largeur du chat"
          onDelta={(dx) => ajuster("chat", dx)}
          onReset={() => remettre("chat")}
        />
        <section className="espace-editeur">
          {navigation}
          <div
            className={`partage ${sansPanneau ? "sans-arbre" : panneauVoulu ? "arbre-voulu" : ""}`}
          >
            <div className="espace-docs">{document}</div>
            <Redimensionneur
              className="sep-arbre"
              libelle={`Largeur de ${libellePanneau}`}
              onDelta={(dx) => ajuster("arbre", dx)}
              onReset={() => remettre("arbre")}
            />
            <aside className="espace-arbre">{panneau}</aside>
          </div>
        </section>
      </div>
    </div>
  );
}
